import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTrustTop } from "../lib/api";
import { TrustBadge, TrustBadgeLightbox } from "../components/trust/TrustBadge";
import { FloralOrnament } from "../components/FloralOrnament";
import { Sparkles, Award, Share2, ArrowRight } from "lucide-react";

// Prefer the runtime origin (works on both preview and prod) with a fallback
// to the configured backend URL if the page is rendered off-DOM.
const SITE_URL =
  (typeof window !== "undefined" && window.location?.origin) ||
  process.env.REACT_APP_PUBLIC_SITE_URL ||
  "https://beckyliss-glossary.com";

export default function TopFormules() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    document.title = "Top formules — Beckyliss Cosmetics";
    // Meta description for SEO
    const meta = document.querySelector('meta[name="description"]') ||
      Object.assign(document.createElement("meta"), { name: "description" });
    meta.setAttribute("content",
      "Découvrez les formules Beckyliss Cosmetics les mieux notées : soins visage, corps, cheveux, maquillage. Analyse INCI et badge de confiance sur 10.");
    if (!meta.parentNode) document.head.appendChild(meta);
    fetchTrustTop(15).then(setData).catch(() => setData(null));
  }, []);

  // JSON-LD structured data for SEO
  useEffect(() => {
    if (!data) return;
    const items = [];
    (data.hero_top || []).forEach((p, i) => items.push({
      "@type": "ListItem", position: i + 1,
      item: {
        "@type": "Product",
        name: p.name,
        url: `${SITE_URL}/produit/${p.slug}`,
        image: p.image_url,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: p.global_score,
          bestRating: 10,
          worstRating: 0,
          ratingCount: 1,
        },
      },
    }));
    const ld = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Top formules Beckyliss Cosmetics",
      itemListElement: items,
    };
    let script = document.getElementById("ld-top-formules");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ld-top-formules";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { script?.remove(); };
  }, [data]);

  if (!data) {
    return (
      <div className="max-w-editorial mx-auto px-6 py-32 text-center text-bl-textSoft" data-testid="top-loading">
        Chargement des meilleures formules…
      </div>
    );
  }

  return (
    <div className="fade-in-up" data-testid="top-formules-page">
      {/* HERO */}
      <section className="relative overflow-hidden hero-bg-gradient border-b border-bl-border">
        <FloralOrnament className="floral-ornament floral-ornament--tr" variant="1" />
        <FloralOrnament className="floral-ornament floral-ornament--bl" variant="2" />
        <div className="relative max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <Award size={16} className="text-bl-dore" strokeWidth={1.4} />
              <span className="overline text-bl-dore">Sélection éditoriale</span>
            </div>
            <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl text-bl-text leading-[1.05] text-balance">
              Nos <em className="italic text-bl-dore">meilleures</em> formules
            </h1>
            <p className="font-serif italic text-xl md:text-2xl text-bl-textSoft mt-6 leading-relaxed">
              Les produits Beckyliss aux compositions les plus abouties, notés par notre badge de confiance.
              Analyse INCI, actifs prouvés, sécurité européenne — chaque formule sur 10.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 text-sm">
              <StatChip value={data.stats?.total_scored ?? 0} label="Produits analysés" />
              <StatChip value={data.stats?.excellent_count ?? 0} label="Formules excellentes ≥ 9/10" />
              <StatChip value={(data.sections || []).length} label="Familles éditoriales" />
            </div>
          </div>
        </div>
      </section>

      {/* HERO TOP 3 PODIUM */}
      {data.hero_top?.length > 0 && (
        <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20" data-testid="top-podium">
          <div className="ornament-diamond mb-4"><span className="diamond" /></div>
          <h2 className="font-serif text-3xl md:text-4xl text-bl-text text-center">
            Podium <em className="italic text-bl-dore">confiance</em>
          </h2>
          <p className="text-center text-bl-textSoft mt-3 max-w-xl mx-auto">
            Les trois formules les mieux notées de tout le catalogue Beckyliss Cosmetics.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {data.hero_top.map((p, i) => (
              <PodiumCard key={p.slug} product={p} rank={i + 1} onOpen={() => setSelected(p)} />
            ))}
          </div>
        </section>
      )}

      {/* SECTIONS by family */}
      {(data.sections || []).map((section) => (
        <section
          key={section.key}
          className="border-t border-bl-border py-20"
          data-testid={`top-section-${section.key}`}
        >
          <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14">
            <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
              <div>
                <span className="overline">Meilleures formules</span>
                <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-2">
                  {section.title}
                </h2>
              </div>
              <ShareButton title={`Top ${section.title} — Beckyliss`} url={`${SITE_URL}/top-formules#${section.key}`} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {section.items.map((p, idx) => (
                <TopCard key={p.slug} product={p} rank={idx + 1} onOpen={() => setSelected(p)} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="hero-bg-gradient border-t border-bl-border py-20">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 text-center">
          <Sparkles className="mx-auto text-bl-dore mb-4" size={22} strokeWidth={1.3} />
          <h2 className="font-serif text-3xl md:text-4xl text-bl-text">
            Trouvez votre <em className="italic text-bl-dore">signature</em>
          </h2>
          <p className="text-bl-textSoft mt-4 max-w-2xl mx-auto">
            Notre quiz beauté vous recommande les meilleures formules Beckyliss selon votre routine.
          </p>
          <Link
            to="/quiz"
            className="mt-8 inline-flex items-center gap-2 bg-bl-text text-bl-bg px-8 py-3.5 text-sm tracking-wide hover:bg-bl-dore transition-colors"
            data-testid="top-cta-quiz"
          >
            Faire le quiz Beckyliss <ArrowRight size={16} strokeWidth={1.4} />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <TrustBadgeLightbox
        open={!!selected}
        onClose={() => setSelected(null)}
        slug={selected?.slug}
      />
    </div>
  );
}

const StatChip = ({ value, label }) => (
  <div className="border border-bl-border px-4 py-2.5 bg-white/40 backdrop-blur-sm">
    <span className="font-serif text-2xl text-bl-fuchsia mr-2">{value}</span>
    <span className="text-xs uppercase tracking-wide text-bl-textSoft">{label}</span>
  </div>
);

const rankColor = (r) => (r === 1 ? "#C5A059" : r === 2 ? "#B7A4B0" : "#B7405E");

const PodiumCard = ({ product, rank, onOpen }) => (
  <Link
    to={`/produit/${product.slug}`}
    className="group block bg-white border border-bl-border overflow-hidden relative"
    data-testid={`podium-card-${rank}`}
    style={{ boxShadow: rank === 1 ? "0 20px 40px -20px rgba(197,160,89,.35)" : "0 12px 28px -14px rgba(74,44,90,.2)" }}
  >
    <div
      aria-hidden
      style={{
        position: "absolute", top: 14, left: 14, zIndex: 2,
        background: rankColor(rank), color: "#fff",
        borderRadius: 999, width: 34, height: 34, display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600,
        boxShadow: "0 4px 12px rgba(0,0,0,.15)",
      }}
    >{rank}</div>
    <div className="aspect-square overflow-hidden bg-bl-soft relative">
      {product.image_url ? (
        <img src={product.image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover product-image-zoom" />
      ) : <div className="w-full h-full grid place-items-center text-bl-textSoft">Beckyliss</div>}
      <div
        style={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
        onClick={(e) => { e.preventDefault(); onOpen(); }}
      >
        <TrustBadge score={product.global_score} size={52} testId={`podium-badge-${product.slug}`} />
      </div>
    </div>
    <div className="p-6">
      <p className="overline mb-2">{product.label}</p>
      <h3 className="font-serif text-xl text-bl-text leading-snug min-h-[3.2rem] group-hover:text-bl-fuchsia transition-colors">
        {product.name}
      </h3>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs tracking-wide text-bl-dore underline-grow">Découvrir</span>
        <span className="font-serif text-2xl" style={{ color: rankColor(rank) }}>{product.global_score.toFixed(1)}/10</span>
      </div>
    </div>
  </Link>
);

const TopCard = ({ product, rank, onOpen }) => (
  <Link
    to={`/produit/${product.slug}`}
    className="group block bg-white border border-bl-border overflow-hidden relative"
    data-testid={`top-card-${product.slug}`}
  >
    <div
      aria-hidden
      style={{
        position: "absolute", top: 10, left: 10, zIndex: 2,
        background: "rgba(255,255,255,0.95)", color: "#4A2C5A",
        borderRadius: 999, minWidth: 24, height: 24, padding: "0 8px",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Playfair Display', serif", fontSize: 11, fontWeight: 600,
        border: "1px solid #E5D6E3",
      }}
    >n°{rank}</div>
    <div className="aspect-square overflow-hidden bg-bl-soft relative">
      {product.image_url ? (
        <img src={product.image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover product-image-zoom" />
      ) : <div className="w-full h-full grid place-items-center text-bl-textSoft">Beckyliss</div>}
      <div
        style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
        onClick={(e) => { e.preventDefault(); onOpen(); }}
      >
        <TrustBadge score={product.global_score} size={42} testId={`top-badge-${product.slug}`} />
      </div>
    </div>
    <div className="p-4">
      <h3 className="font-serif text-base text-bl-text leading-snug min-h-[2.6rem] group-hover:text-bl-fuchsia transition-colors line-clamp-2">
        {product.name}
      </h3>
      {product.pills?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {product.pills.slice(0, 3).map((p) => (
            <span
              key={p}
              style={{
                background: "#FDF8FC", border: "1px solid #E8D0E8", color: "#9B4A6B",
                borderRadius: 999, padding: "3px 8px", fontSize: 9.5,
                fontFamily: "'Poppins', sans-serif", fontWeight: 500,
              }}
            >{p}</span>
          ))}
        </div>
      )}
    </div>
  </Link>
);

const ShareButton = ({ title, url }) => {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch { /* dismissed */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard failed */ }
  };
  return (
    <button
      onClick={share}
      className="inline-flex items-center gap-2 text-xs tracking-wide text-bl-mauveDeep hover:text-bl-fuchsia transition-colors"
      data-testid="top-share-btn"
    >
      <Share2 size={13} strokeWidth={1.4} />
      {copied ? "Lien copié !" : "Partager"}
    </button>
  );
};
